import { useState, useEffect, useCallback, useRef } from 'react';
import api from 'services/api';

const AsyncWrapper = (Component) =>
    (function Comp(field, methods, f) {
        const fieldConfig = {
            // eslint-disable-next-line no-unused-vars
            render: ({ execute, status, data, field, methods, f, Component }) => {
                const options = status === 'success' ? data.data.data : [];
                field.props = { ...field.props, onOpen: execute };
                return <Component field={{ ...field, options }} methods={methods} />;
            },
            ...field
        };
        const asyncConfig = fieldConfig.async || {};
        const url = asyncConfig.url ?? '';
        const config = asyncConfig.config ?? {};
        const immediate = asyncConfig.immediate ?? false;

        const [status, setStatus] = useState('idle');
        const [data, setData] = useState(null);
        const [error, setError] = useState(null);

        // Track if initial fetch has been done
        const initialFetchDone = useRef(false);

        // Store methods in ref to avoid dependency changes
        const methodsRef = useRef(methods);
        methodsRef.current = methods;

        // Store async.params in ref
        const asyncParamsRef = useRef(asyncConfig.params);
        asyncParamsRef.current = asyncConfig.params;

        // Store immediate in ref
        const immediateRef = useRef(immediate);
        immediateRef.current = immediate;

        // Get current params - uses refs to avoid recreating
        const getCurrentParams = useCallback(() => {
            const formValues = methodsRef.current?.getValues?.() || {};
            return Object.entries(asyncParamsRef.current ?? {}).reduce((acc, current) => {
                const [key, value] = current;
                acc[key] = value;
                if (typeof value === 'function') {
                    acc[key] = value(formValues);
                }
                return acc;
            }, {});
        }, []);

        // Store config in ref to avoid recreating execute on every render
        const configRef = useRef(config);
        configRef.current = config;

        const execute = useCallback((customParams) => {
            if (!url) return Promise.resolve();
            const params = customParams || getCurrentParams();
            setStatus('pending');
            setData(null);
            setError(null);
            return api.get(url, { params, ...configRef.current })
                .then((response) => {
                    setData(response);
                    setStatus('success');
                })
                .catch((err) => {
                    setError(err);
                    setStatus('error');
                });
        }, [url, getCurrentParams]);

        // Store execute in ref so the immediate effect always calls the latest version
        const executeRef = useRef(execute);
        executeRef.current = execute;

        // Execute immediately when configured — re-runs when immediate transitions to true
        useEffect(() => {
            if (immediate && !initialFetchDone.current) {
                initialFetchDone.current = true;
                executeRef.current();
            }
        }, [immediate]);

        return fieldConfig.render({ execute, status, data, error, field, methods, f, Component });
    });

export default AsyncWrapper;
